import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash2, Save, Beaker, Pill, X, Star, Droplet, Search, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNITS = ["mg", "mcg", "g", "mEq", "units"];
const TYPES = [
  { id: "stock",     label: "Stock Vial",   color: "border-blue-500 bg-blue-500/15 text-blue-300",   help: "Vial as supplied (e.g., epinephrine 1 mg/mL)" },
  { id: "dilution",  label: "Syringe Dilution", color: "border-amber-500 bg-amber-500/15 text-amber-300", help: "Dilute stock in a syringe (e.g., 1 mL stock + 4 mL NS)" },
  { id: "infusion",  label: "IV Drip",      color: "border-purple-500 bg-purple-500/15 text-purple-300", help: "Drug mixed into a bag for infusion (e.g., 4 mg in 250 mL D5W)" },
];

const blankOption = () => ({ conc: "", unit: "mg", label: "", default: false, type: "stock" });
const blankDosing = () => ({ min_dose: 0, max_dose: 1, typical_start: 0.1, dose_unit: "mcg/kg/min" });

function calcDilutionConc(stock_conc, draw_ml, total_ml) {
  if (!stock_conc || !draw_ml || !total_ml || total_ml <= 0) return null;
  return (parseFloat(stock_conc) * parseFloat(draw_ml)) / parseFloat(total_ml);
}
function calcInfusionConc(dose_amount, bag_volume_ml) {
  if (!dose_amount || !bag_volume_ml || bag_volume_ml <= 0) return null;
  return parseFloat(dose_amount) / parseFloat(bag_volume_ml);
}

const TypePicker = ({ value, onChange, testIdPrefix }) => (
  <div className="grid grid-cols-3 gap-1.5">
    {TYPES.map((t) => (
      <button
        key={t.id}
        onClick={() => onChange(t.id)}
        type="button"
        className={`px-2 py-1.5 rounded-md text-xs font-bold border transition-all ${value === t.id ? t.color : "bg-secondary/30 text-muted-foreground border-border hover:bg-secondary/50"}`}
        data-testid={`${testIdPrefix}-type-${t.id}`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const RecipeFields = ({ option, onChange }) => {
  if (option.type === "stock") return null;
  if (option.type === "dilution") {
    const r = option.recipe || {};
    return (
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-md p-3 space-y-2 mt-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-300">
          <Beaker className="w-3.5 h-3.5" /> Syringe Recipe
        </div>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-3 space-y-1">
            <label className="text-[10px] text-muted-foreground block">Stock conc</label>
            <Input type="number" step="0.1" value={r.stock_conc ?? ""} onChange={(e) => onChange("stock_conc", e.target.value)} className="h-9 text-sm" placeholder="e.g. 50" />
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] text-muted-foreground block">Unit</label>
            <select suppressHydrationWarning value={r.stock_unit || "mg"} onChange={(e) => onChange("stock_unit", e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
              {UNITS.map((u) => <option key={u} value={u} suppressHydrationWarning>{u}/mL</option>)}
            </select>
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] text-muted-foreground block">Draw mL</label>
            <Input type="number" step="0.1" value={r.draw_ml ?? ""} onChange={(e) => onChange("draw_ml", e.target.value)} className="h-9 text-sm" placeholder="1" />
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] text-muted-foreground block">Final mL</label>
            <Input type="number" step="0.1" value={r.total_ml ?? ""} onChange={(e) => onChange("total_ml", e.target.value)} className="h-9 text-sm" placeholder="5" />
          </div>
          <div className="col-span-3 space-y-1">
            <label className="text-[10px] text-muted-foreground block">Diluent</label>
            <Input value={r.diluent || ""} placeholder="NS" onChange={(e) => onChange("diluent", e.target.value)} className="h-9 text-sm" />
          </div>
        </div>
        <div className="text-[11px] text-amber-200 italic">
          Draw {r.draw_ml || "?"} mL of {r.stock_conc || "?"} {r.stock_unit || "?"}/mL stock → bring to {r.total_ml || "?"} mL with {r.diluent || "diluent"}
        </div>
      </div>
    );
  }
  // infusion
  const r = option.recipe || {};
  return (
    <div className="bg-purple-500/5 border border-purple-500/30 rounded-md p-3 space-y-2 mt-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-300">
        <Droplet className="w-3.5 h-3.5" /> IV Bag Recipe
      </div>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-3 space-y-1">
          <label className="text-[10px] text-muted-foreground block">Drug amt</label>
          <Input type="number" step="0.1" value={r.dose_amount ?? ""} onChange={(e) => onChange("dose_amount", e.target.value)} className="h-9 text-sm" placeholder="4" />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-[10px] text-muted-foreground block">Unit</label>
          <select suppressHydrationWarning value={r.dose_unit || "mg"} onChange={(e) => onChange("dose_unit", e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
            {UNITS.map((u) => <option key={u} value={u} suppressHydrationWarning>{u}</option>)}
          </select>
        </div>
        <div className="col-span-3 space-y-1">
          <label className="text-[10px] text-muted-foreground block">Bag size mL</label>
          <Input type="number" step="1" value={r.bag_volume_ml ?? ""} onChange={(e) => onChange("bag_volume_ml", e.target.value)} className="h-9 text-sm" placeholder="250" />
        </div>
        <div className="col-span-4 space-y-1">
          <label className="text-[10px] text-muted-foreground block">Diluent</label>
          <Input value={r.diluent || ""} placeholder="D5W or NS" onChange={(e) => onChange("diluent", e.target.value)} className="h-9 text-sm" />
        </div>
      </div>
      <div className="text-[11px] text-purple-200 italic">
        Mix {r.dose_amount || "?"} {r.dose_unit || "?"} into {r.bag_volume_ml || "?"} mL bag of {r.diluent || "diluent"}
      </div>
    </div>
  );
};

const AdminConcentrations = () => {
  const { token } = useAuth();
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDrug, setNewDrug] = useState("");
  const [newOptions, setNewOptions] = useState([blankOption()]);
  const [newDosing, setNewDosing] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/drug-concentrations`);
      const sorted = (res.data || []).sort((a, b) => a.drug.localeCompare(b.drug));
      setDrugs(sorted);
      const editState = {};
      sorted.forEach((d) => {
        editState[d.drug] = {
          options: JSON.parse(JSON.stringify(d.options.map((o) => ({ type: "stock", ...o })))),
          infusion_dosing: d.infusion_dosing ? { ...d.infusion_dosing } : null,
        };
      });
      setEditing(editState);
    } catch {
      toast.error("Failed to load drug concentrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredDrugs = drugs.filter((d) => d.drug.toLowerCase().includes(search.toLowerCase()));

  const updateOption = (drug, idx, field, value) => {
    setEditing((prev) => {
      const opts = [...prev[drug].options];
      const next = { ...opts[idx] };
      if (field === "conc") next.conc = value === "" ? "" : parseFloat(value);
      else if (field === "type") {
        next.type = value;
        if (value === "stock") delete next.recipe;
        else if (value === "dilution") next.recipe = next.recipe?.draw_ml ? next.recipe : { stock_conc: next.conc || 50, stock_unit: next.unit, draw_ml: 1, total_ml: 5, diluent: "NS" };
        else next.recipe = next.recipe?.bag_volume_ml ? next.recipe : { dose_amount: next.conc || 1, dose_unit: next.unit, bag_volume_ml: 100, diluent: "NS or D5W" };
      } else next[field] = value;

      if (next.type === "dilution" && next.recipe) {
        const c = calcDilutionConc(next.recipe.stock_conc, next.recipe.draw_ml, next.recipe.total_ml);
        if (c !== null) { next.conc = parseFloat(c.toFixed(2)); next.unit = next.recipe.stock_unit || next.unit; }
      } else if (next.type === "infusion" && next.recipe) {
        const c = calcInfusionConc(next.recipe.dose_amount, next.recipe.bag_volume_ml);
        if (c !== null) { next.conc = parseFloat(c.toFixed(3)); next.unit = next.recipe.dose_unit || next.unit; }
      }
      opts[idx] = next;
      return { ...prev, [drug]: { ...prev[drug], options: opts } };
    });
  };

  const updateRecipe = (drug, idx, field, value) => {
    setEditing((prev) => {
      const opts = [...prev[drug].options];
      const recipe = { ...(opts[idx].recipe || {}), [field]: ["draw_ml","total_ml","stock_conc","dose_amount","bag_volume_ml"].includes(field) ? (value === "" ? "" : parseFloat(value)) : value };
      const next = { ...opts[idx], recipe };
      if (next.type === "dilution") {
        const c = calcDilutionConc(recipe.stock_conc, recipe.draw_ml, recipe.total_ml);
        if (c !== null) next.conc = parseFloat(c.toFixed(2));
        if (recipe.stock_unit) next.unit = recipe.stock_unit;
      } else if (next.type === "infusion") {
        const c = calcInfusionConc(recipe.dose_amount, recipe.bag_volume_ml);
        if (c !== null) next.conc = parseFloat(c.toFixed(3));
        if (recipe.dose_unit) next.unit = recipe.dose_unit;
      }
      opts[idx] = next;
      return { ...prev, [drug]: { ...prev[drug], options: opts } };
    });
  };

  const setDefault = (drug, idx) => {
    setEditing((prev) => ({ ...prev, [drug]: { ...prev[drug], options: prev[drug].options.map((o, i) => ({ ...o, default: i === idx })) } }));
  };
  const addOption = (drug) => {
    setEditing((prev) => ({ ...prev, [drug]: { ...prev[drug], options: [...prev[drug].options, blankOption()] } }));
  };
  const removeOption = (drug, idx) => {
    setEditing((prev) => {
      const opts = prev[drug].options.filter((_, i) => i !== idx);
      if (opts.length && !opts.some((o) => o.default)) opts[0].default = true;
      return { ...prev, [drug]: { ...prev[drug], options: opts } };
    });
  };
  const toggleDosing = (drug) => {
    setEditing((prev) => ({ ...prev, [drug]: { ...prev[drug], infusion_dosing: prev[drug].infusion_dosing ? null : blankDosing() } }));
  };
  const updateDosing = (drug, field, value) => {
    setEditing((prev) => ({ ...prev, [drug]: { ...prev[drug], infusion_dosing: { ...prev[drug].infusion_dosing, [field]: field === "dose_unit" ? value : parseFloat(value) } } }));
  };

  const saveDrug = async (drug) => {
    const state = editing[drug];
    const opts = state.options;
    if (!opts || opts.length === 0) { toast.error("At least one concentration required"); return; }
    for (const o of opts) {
      if (!o.conc || !o.unit || !o.label.trim()) { toast.error("Each option needs concentration, unit, and label"); return; }
    }
    try {
      await axios.put(`${API}/admin/drug-concentrations/${drug}`, { options: opts, infusion_dosing: state.infusion_dosing || null }, auth);
      toast.success(`Saved ${drug}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const deleteDrug = async (drug) => {
    if (!window.confirm(`Delete all concentrations for "${drug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/drug-concentrations/${drug}`, auth);
      toast.success(`Deleted ${drug}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const addNewDrug = async () => {
    const drug = newDrug.trim().toLowerCase();
    if (!drug) { toast.error("Drug name required"); return; }
    const opts = newOptions.filter((o) => o.conc && o.label.trim());
    if (opts.length === 0) { toast.error("At least one valid option required"); return; }
    if (!opts.some((o) => o.default)) opts[0].default = true;
    try {
      const body = { drug, options: opts };
      if (newDosing) body.infusion_dosing = newDosing;
      await axios.post(`${API}/admin/drug-concentrations`, body, auth);
      toast.success(`Added ${drug}`);
      setNewDrug("");
      setNewOptions([blankOption()]);
      setNewDosing(null);
      setShowAddForm(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Add failed"); }
  };

  const renderOptionEditor = (drug, opt, idx, totalOpts, onUpdateOption, onUpdateRecipe, onSetDefault, onRemove) => (
    <div key={idx} className={`rounded-lg border-2 p-3 space-y-2 ${opt.default ? "border-primary/40 bg-primary/5" : "border-border bg-secondary/10"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="outline" className="text-[10px] tabular-nums">Option {idx + 1}</Badge>
          {opt.default && <Badge className="bg-primary text-primary-foreground text-[10px]">DEFAULT</Badge>}
        </div>
        <div className="flex gap-1">
          <Button variant={opt.default ? "default" : "outline"} size="icon" className="h-7 w-7" onClick={() => onSetDefault(idx)} title="Set default">
            <Star className={`w-3.5 h-3.5 ${opt.default ? "fill-current" : ""}`} />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onRemove(idx)} disabled={totalOpts === 1}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <TypePicker value={opt.type || "stock"} onChange={(v) => onUpdateOption(idx, "type", v)} testIdPrefix={`${drug}-${idx}`} />

      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-7 space-y-1">
          <label className="text-[10px] text-muted-foreground block uppercase tracking-wide">Display label (what medics see)</label>
          <Input value={opt.label} onChange={(e) => onUpdateOption(idx, "label", e.target.value)} className="h-9" placeholder='e.g., "1 mg/mL stock vial"' />
        </div>
        <div className="col-span-3 space-y-1">
          <label className="text-[10px] text-muted-foreground block uppercase tracking-wide">Conc</label>
          <Input type="number" step="0.01" value={opt.conc} onChange={(e) => onUpdateOption(idx, "conc", e.target.value)} className="h-9 font-mono" disabled={opt.type !== "stock"} />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-[10px] text-muted-foreground block uppercase tracking-wide">Unit</label>
          <select suppressHydrationWarning value={opt.unit} onChange={(e) => onUpdateOption(idx, "unit", e.target.value)} disabled={opt.type !== "stock"} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50">
            {UNITS.map((u) => <option key={u} value={u} suppressHydrationWarning>{u}/mL</option>)}
          </select>
        </div>
      </div>

      {opt.type !== "stock" && (
        <p className="text-[11px] text-muted-foreground italic flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          Concentration auto-calculates from the recipe below.
        </p>
      )}

      <RecipeFields option={opt} onChange={(field, value) => onUpdateRecipe(idx, field, value)} />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/admin/dashboard"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Dashboard</Button></Link>
          <h1 className="text-xl font-heading font-bold flex items-center gap-2">
            <Beaker className="w-5 h-5" />Drug Concentrations
          </h1>
          <Badge variant="outline" className="ml-auto">{drugs.length} drugs</Badge>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-4">
        {/* Help banner */}
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="p-3 text-xs space-y-1">
            <div className="font-semibold text-blue-300 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> How this works</div>
            <p className="text-muted-foreground leading-relaxed">
              Each drug can have multiple concentration <strong>options</strong>. Three types are supported:
            </p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
              <li><span className="text-blue-300 font-medium">Stock Vial</span> — vial as supplied (e.g., epi 1 mg/mL)</li>
              <li><span className="text-amber-300 font-medium">Syringe Dilution</span> — dilute stock in a syringe (e.g., 1 mL stock + 4 mL NS)</li>
              <li><span className="text-purple-300 font-medium">IV Drip</span> — mix into bag (e.g., 4 mg into 250 mL D5W)</li>
            </ul>
            <p className="text-muted-foreground">For drips, also enable <strong>Infusion Dosing</strong> below to power the protocol-page pump-rate calculator.</p>
          </CardContent>
        </Card>

        {/* Search + Add */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search drug..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="conc-search" />
          </div>
          <Button onClick={() => setShowAddForm((v) => !v)} className="h-10" data-testid="add-drug-toggle">
            <Plus className="w-4 h-4 mr-1" />{showAddForm ? "Cancel" : "Add Drug"}
          </Button>
        </div>

        {/* Add new drug form */}
        {showAddForm && (
          <Card className="border-emerald-500/40 bg-emerald-500/5">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-400" />Add New Drug</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground block uppercase tracking-wide">Drug name (lowercase, e.g., epinephrine)</label>
                <Input placeholder="drug name" value={newDrug} onChange={(e) => setNewDrug(e.target.value)} className="h-10" data-testid="new-drug-name" />
              </div>
              <div className="space-y-2">
                {newOptions.map((opt, idx) => renderOptionEditor(
                  "_new", opt, idx, newOptions.length,
                  (i, field, value) => setNewOptions((prev) => prev.map((o, j) => {
                    if (j !== i) return o;
                    const next = { ...o };
                    if (field === "conc") next.conc = value === "" ? "" : parseFloat(value);
                    else if (field === "type") {
                      next.type = value;
                      if (value === "stock") delete next.recipe;
                      else if (value === "dilution") next.recipe = next.recipe || { stock_conc: 50, stock_unit: "mg", draw_ml: 1, total_ml: 5, diluent: "NS" };
                      else next.recipe = next.recipe || { dose_amount: 1, dose_unit: "mg", bag_volume_ml: 100, diluent: "D5W or NS" };
                    } else next[field] = value;
                    if (next.type === "dilution" && next.recipe) {
                      const c = calcDilutionConc(next.recipe.stock_conc, next.recipe.draw_ml, next.recipe.total_ml);
                      if (c !== null) { next.conc = parseFloat(c.toFixed(2)); next.unit = next.recipe.stock_unit || next.unit; }
                    } else if (next.type === "infusion" && next.recipe) {
                      const c = calcInfusionConc(next.recipe.dose_amount, next.recipe.bag_volume_ml);
                      if (c !== null) { next.conc = parseFloat(c.toFixed(3)); next.unit = next.recipe.dose_unit || next.unit; }
                    }
                    return next;
                  })),
                  (i, field, value) => setNewOptions((prev) => prev.map((o, j) => {
                    if (j !== i) return o;
                    const recipe = { ...(o.recipe || {}), [field]: ["draw_ml","total_ml","stock_conc","dose_amount","bag_volume_ml"].includes(field) ? (value === "" ? "" : parseFloat(value)) : value };
                    const next = { ...o, recipe };
                    if (next.type === "dilution") {
                      const c = calcDilutionConc(recipe.stock_conc, recipe.draw_ml, recipe.total_ml);
                      if (c !== null) next.conc = parseFloat(c.toFixed(2));
                      if (recipe.stock_unit) next.unit = recipe.stock_unit;
                    } else if (next.type === "infusion") {
                      const c = calcInfusionConc(recipe.dose_amount, recipe.bag_volume_ml);
                      if (c !== null) next.conc = parseFloat(c.toFixed(3));
                      if (recipe.dose_unit) next.unit = recipe.dose_unit;
                    }
                    return next;
                  })),
                  (i) => setNewOptions((prev) => prev.map((o, j) => ({ ...o, default: j === i }))),
                  (i) => setNewOptions((prev) => prev.filter((_, j) => j !== i)),
                ))}
                <Button variant="outline" size="sm" onClick={() => setNewOptions((prev) => [...prev, blankOption()])} className="w-full">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add another option
                </Button>
              </div>

              {/* Optional infusion dosing */}
              <div className="border rounded-lg p-3 bg-purple-500/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-purple-300">
                    <Droplet className="w-4 h-4" />Infusion Dosing Range
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setNewDosing(newDosing ? null : blankDosing())}>
                    {newDosing ? "Disable" : "Enable"}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Enables the pump-rate calculator on protocol pages. Skip if this drug isn't run as a drip.</p>
                {newDosing && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3">
                    <div><label className="text-[10px] text-muted-foreground block">Min</label><Input type="number" step="0.01" value={newDosing.min_dose} onChange={(e) => setNewDosing({ ...newDosing, min_dose: parseFloat(e.target.value) })} className="h-9" /></div>
                    <div><label className="text-[10px] text-muted-foreground block">Max</label><Input type="number" step="0.01" value={newDosing.max_dose} onChange={(e) => setNewDosing({ ...newDosing, max_dose: parseFloat(e.target.value) })} className="h-9" /></div>
                    <div><label className="text-[10px] text-muted-foreground block">Typical start</label><Input type="number" step="0.01" value={newDosing.typical_start} onChange={(e) => setNewDosing({ ...newDosing, typical_start: parseFloat(e.target.value) })} className="h-9" /></div>
                    <div><label className="text-[10px] text-muted-foreground block">Unit</label><Input value={newDosing.dose_unit} onChange={(e) => setNewDosing({ ...newDosing, dose_unit: e.target.value })} className="h-9" placeholder="mcg/kg/min" /></div>
                  </div>
                )}
              </div>

              <Button onClick={addNewDrug} className="w-full bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="add-drug-btn">
                <Plus className="w-4 h-4 mr-1" />Add Drug
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Drugs list */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading drugs…</div>
        ) : filteredDrugs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="no-results">
            {search ? `No drugs match "${search}"` : "No drugs configured."}
          </div>
        ) : (
          <div className="space-y-3" data-testid="drug-list">
            {filteredDrugs.map((d) => {
              const state = editing[d.drug] || { options: [], infusion_dosing: null };
              return (
                <Card key={d.drug} className="overflow-hidden">
                  <CardHeader className="pb-3 bg-secondary/20">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CardTitle className="text-base flex items-center gap-2 capitalize">
                        <Pill className="w-4 h-4 text-primary" />
                        {d.drug}
                        <Badge variant="outline" className="text-[10px]">{state.options.length} option{state.options.length !== 1 ? "s" : ""}</Badge>
                        {state.infusion_dosing && <Badge className="text-[10px] bg-purple-600">drip</Badge>}
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveDrug(d.drug)} data-testid={`save-${d.drug}`}>
                          <Save className="w-3.5 h-3.5 mr-1" />Save
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => deleteDrug(d.drug)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-3 space-y-2">
                    {state.options.map((opt, idx) => renderOptionEditor(
                      d.drug, opt, idx, state.options.length,
                      (i, field, value) => updateOption(d.drug, i, field, value),
                      (i, field, value) => updateRecipe(d.drug, i, field, value),
                      (i) => setDefault(d.drug, i),
                      (i) => removeOption(d.drug, i),
                    ))}
                    <Button variant="ghost" size="sm" onClick={() => addOption(d.drug)} className="w-full">
                      <Plus className="w-3.5 h-3.5 mr-1" />Add option
                    </Button>

                    {/* Infusion dosing */}
                    <div className="border rounded-lg p-3 bg-purple-500/5 mt-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-purple-300">
                          <Droplet className="w-4 h-4" />Infusion Dosing Range
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleDosing(d.drug)} data-testid={`dosing-toggle-${d.drug}`}>
                          {state.infusion_dosing ? "Disable" : "Enable"}
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Powers the pump-rate calculator on protocol pages.</p>
                      {state.infusion_dosing && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3">
                          <div><label className="text-[10px] text-muted-foreground block">Min</label><Input type="number" step="0.01" value={state.infusion_dosing.min_dose} onChange={(e) => updateDosing(d.drug, "min_dose", e.target.value)} className="h-9" /></div>
                          <div><label className="text-[10px] text-muted-foreground block">Max</label><Input type="number" step="0.01" value={state.infusion_dosing.max_dose} onChange={(e) => updateDosing(d.drug, "max_dose", e.target.value)} className="h-9" /></div>
                          <div><label className="text-[10px] text-muted-foreground block">Typical start</label><Input type="number" step="0.01" value={state.infusion_dosing.typical_start} onChange={(e) => updateDosing(d.drug, "typical_start", e.target.value)} className="h-9" /></div>
                          <div><label className="text-[10px] text-muted-foreground block">Unit</label><Input value={state.infusion_dosing.dose_unit} onChange={(e) => updateDosing(d.drug, "dose_unit", e.target.value)} className="h-9" placeholder="mcg/kg/min" /></div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConcentrations;
